import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

interface KDEPlotProps {
    events: any[];
    startTime: string;
    endTime: string;
    width: any;
}

// Add a density graph component using Kernel Density Estimation
const KDEPlot: React.FC<KDEPlotProps> = ({ events , startTime, endTime, width}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const [dimensions, setDimensions] = useState({ width: width, height: 35 }); // Default dimensions for the SVG

    useEffect(() => {
        // Set up a resize observer to update SVG dimensions whenever the container size changes
        const observeTarget = svgRef.current;
        const resizeObserver = new ResizeObserver(entries => {
            if (!Array.isArray(entries) || !entries.length) return;
            const entry = entries[0];
            setDimensions({
                width: entry.contentRect.width,
                height: entry.contentRect.height
            });
        });

        // Begin observing
        if (observeTarget) {
            resizeObserver.observe(observeTarget);
        }

        // Clean up the observation when done
        return () => {
            if (observeTarget) {
                resizeObserver.unobserve(observeTarget);
            }
        };
    }, []);

    useEffect(() => {
        // Extract event timestamps and convert to seconds since the first event
        const startTimestamp = new Date(startTime).getTime();
        const endTimestamp = new Date(endTime).getTime();
        const timestamps = events.map(e => new Date(e.timestamp).getTime() - startTimestamp);
        const minTime = Math.min(...timestamps);
        const timesInSeconds = timestamps.map(t => (t - minTime) / 1000);

        // Define Kernel Density Estimation functions
        function kernelDensityEstimation(values: number[], kernel: (u: number) => number, bandwidth: number) {
            return (x: number) => {
                return Array.from(values).reduce((sum, v) => {
                    return sum + kernel((v - x) / bandwidth);
                }, 0) / values.length;
            };
        }

        function gaussianKernel(u: number) {
            return (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * u * u);
        }

        function estimateBandwidth(data: number[]): number {
            const n = data.length;
            const sigma = standardDeviation(data);
            return Math.pow((4 * Math.pow(sigma, 5) / (3 * n)), 0.2);
        }

        function standardDeviation(values: number[]): number {
            const n = values.length;
            const mean = values.reduce((a, b) => a + b) / n;
            return Math.sqrt(values.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / n);
        }

        // Calculate bandwidth and KDE
        const bandwidth = estimateBandwidth(timesInSeconds);
        const kde = kernelDensityEstimation(timesInSeconds, gaussianKernel, bandwidth * 0.15);

        if (svgRef.current) {
            const svg = d3.select(svgRef.current);

            const width = dimensions.width;
            const height = dimensions.height;

            // Set up scales and D3 line generator
            const x = d3.scaleLinear()
                .domain([0, (endTimestamp - startTimestamp) / 1000])
                .range([0, width]);

            const y = d3.scaleLinear().range([height, 0]);

            const line = d3.line<[number, number]>()
                .x(d => x(d[0]))
                .y(d => y(d[1]));

            // Calculate KDE values at reduced resolution (every 60 seconds)
            // Change step variable to increase/decrease density of graph
            const STEP_SIZE = 60;
            const durationInSeconds = (endTimestamp - startTimestamp) / 1000;
            const density: [number, number][] = [];
            for (let i = 0; i <= durationInSeconds; i += STEP_SIZE) {
                density.push([i, kde(i)]);
            }

            const yMin: any = d3.min(density, d => d[1]);
            const yMax: any = d3.max(density, d => d[1]);
            const buffer = 1.05;

            y.domain([yMin, yMax * buffer]);

            // Apply transitions for smooth appearance
            const t = d3.transition().duration(750);

            // Draw the KDE path
            const paths = svg.selectAll("path").data([density]);

            // Update path if it already exists
            paths.attr("d", line).transition(t);

            // Create path if it doesn't exist yet
            paths.enter()
                .append("path")
                .attr("fill", "url(#kdeGradient)")
                .attr("stroke", "#2c2c2c")
                .attr("stroke-width", 1.5)
                .attr("stroke-linejoin", "round")
                .attr("stroke-linecap", "round")
                .transition(t)
                .attr("opacity", ".9")
                .attr("d", line);
        }
    }, [events, dimensions]);

    return (
        // Height stays fixed as the menu bar size is fixed, graph is dynamic
        <svg ref={svgRef} width="100%" data-testid="kdeplot-svg" height="35"></svg>
    );
}

export default KDEPlot;
